import React, { useEffect, useState } from "react";
import {
  CAlert,
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormSelect,
  CRow,
  CSpinner,
} from "@coreui/react";
import type { ApiStudyCandidate, ApiStudyCandidateSelector } from "@tradingdroid/frontends-types";
import { CDataTable } from "@tradingdroid/frontends-components";
import { tdApi } from "@src/services/api/tdApi";

import { ColoredValue } from "@src/components/ColoredValue";

interface Props {
  studyId: number;
}

const SELECTOR_LABEL: Record<ApiStudyCandidateSelector, string> = {
  best_objective: "Best objective",
  best_plateau: "Best plateau (most stable)",
};

const CANDIDATE_COUNT = 10;

/** Compact "key: value" summary of a trial's tuned parameters. */
const ParamsSummary: React.FC<{ params: Record<string, unknown> }> = ({ params }) => {
  const entries = Object.entries(params);
  if (entries.length === 0) return <span className="text-body-secondary">—</span>;
  return (
    <div className="StudyTopCandidates-params d-flex flex-wrap gap-1">
      {entries.map(([key, value]) => (
        <CBadge key={key} color="secondary" className="fw-normal">
          {key}: {String(value)}
        </CBadge>
      ))}
    </div>
  );
};

/**
 * Study top-K candidates, ranked over the FULL finished-trial set. This is a global ranking, so a
 * candidate here may not appear in the per-metric selected-trials table above it.
 */
export const StudyTopCandidates: React.FC<Props> = ({ studyId }) => {
  const [selector, setSelector] = useState<ApiStudyCandidateSelector>("best_objective");
  const [candidates, setCandidates] = useState<ApiStudyCandidate[]>([]);
  const [finishedTrials, setFinishedTrials] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await tdApi.getStudyCandidates(studyId, {
          selector,
          k: CANDIDATE_COUNT,
        });
        if (cancelled) return;
        setCandidates(result?.candidates ?? []);
        setFinishedTrials(result?.finished_trials ?? null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [studyId, selector]);

  const columns = [
    {
      columnKey: "candidate_rank",
      columnLabel: "Rank",
      columnStyle: { width: "60px", textAlign: "right" as const },
      cellRender: (c: ApiStudyCandidate) => <ColoredValue value={c.candidate_rank} type="flat" />,
    },
    {
      columnKey: "trial_number",
      columnLabel: "Trial #",
      columnStyle: { width: "80px", textAlign: "right" as const },
      cellRender: (c: ApiStudyCandidate) => <ColoredValue value={c.trial_number} type="flat" />,
    },
    {
      columnKey: "objective_value",
      columnLabel: "Objective",
      columnStyle: { width: "120px", textAlign: "right" as const },
      // Flat, not colored against zero: "better" depends on the study direction (MINIMIZE flips it) and
      // zero is not a meaningful baseline for most objectives. The rank column already conveys ordering.
      cellRender: (c: ApiStudyCandidate) =>
        c.objective_value == null ? (
          <span className="text-body-secondary">—</span>
        ) : (
          <ColoredValue value={c.objective_value} type="flat" />
        ),
    },
    {
      columnKey: "params",
      columnLabel: "Parameters",
      cellRender: (c: ApiStudyCandidate) => <ParamsSummary params={c.params} />,
    },
  ];

  return (
    <CCard className="StudyTopCandidates mb-4">
      <CCardHeader className="StudyTopCandidatesHeader">
        <CRow className="align-items-center">
          <CCol>
            <h6 className="mb-0">
              Top Candidates{" "}
              <span className="text-body-secondary fw-normal fs-sm">
                Ranked over all {finishedTrials ?? "?"} finished trials, so a candidate may not be
                in the selected-trials table above
              </span>
            </h6>
          </CCol>
          <CCol xs="auto">
            <div className="StudyTopCandidatesSelect d-flex align-items-center gap-2">
              <label htmlFor="candidateSelector" className="mb-0">
                Rank by:
              </label>
              <CFormSelect
                id="candidateSelector"
                size="sm"
                value={selector}
                onChange={(e) => setSelector(e.target.value as ApiStudyCandidateSelector)}
              >
                {(Object.keys(SELECTOR_LABEL) as ApiStudyCandidateSelector[]).map((key) => (
                  <option key={key} value={key}>
                    {SELECTOR_LABEL[key]}
                  </option>
                ))}
              </CFormSelect>
            </div>
          </CCol>
        </CRow>
      </CCardHeader>
      <CCardBody className="StudyTopCandidatesBody p-0">
        {error ? (
          <CAlert color="warning" className="m-3">
            Could not load candidates: {error}
          </CAlert>
        ) : loading ? (
          <div className="d-flex align-items-center gap-2 p-3">
            <CSpinner size="sm" /> Ranking candidates...
          </div>
        ) : (
          <CDataTable
            columns={columns}
            items={candidates}
            getRowKey={(c) => c.trial_number}
            striped
            small
            hover
            responsive
          />
        )}
      </CCardBody>
    </CCard>
  );
};
